import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../Icon';
import { ghostBtn, primaryBtn } from './ui';
import { CHALLENGE_CONTENT, challengeById } from '../../lib/assessment';

const RULES = CHALLENGE_CONTENT.rules;

const yesterdayISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

export const ChallengePanel = ({ active, recommendedId, onJoin, onDone, busy, isAuthed }) => {
  const challenge = challengeById(active?.challenge_id || recommendedId);
  if (!challenge) return null;

  const dayIndex = active ? active.current_day : 1;
  const dayText = challenge.days[dayIndex - 1];
  const dates = active ? Object.values(active.days || {}) : [];
  const lastDone = dates.length ? dates.sort().slice(-1)[0] : null;
  const missed = !!active && !active.complete && dates.length > 0
    && !active.done_today && lastDone < yesterdayISO();

  return (
    <div className="bg-surface p-space-lg lg:p-space-xl rounded-lg shadow-sm" data-testid="challenge-panel">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-space-sm mb-space-md">
        <div>
          <span className="font-label-md text-label-md uppercase tracking-widest text-surface-tint font-bold block">
            {active ? 'MY CHALLENGE' : 'RECOMMENDED CHALLENGE'}
          </span>
          <h2 className="font-headline-sm text-headline-sm uppercase text-outline" data-testid="challenge-name">{challenge.name}</h2>
        </div>
        {active && !active.complete ? (
          <span className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant" data-testid="challenge-day-count">
            Day {dayIndex} of {active.total_days}
          </span>
        ) : null}
      </div>

      {active?.complete ? (
        <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed" data-testid="challenge-finished">
          {RULES.finish_text.replace('[name]', challenge.name)}
        </p>
      ) : (
        <>
          {missed ? (
            <p className="font-body-sm text-body-sm text-surface-tint font-semibold mb-space-sm" data-testid="challenge-missed">{RULES.missed_day_text}</p>
          ) : null}
          <div className="p-space-md rounded-lg bg-primary-container border-l-4 border-surface-tint mb-space-md">
            <span className="font-label-sm text-label-sm uppercase tracking-wider text-outline font-bold block mb-space-xs">Day {dayIndex}</span>
            <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed" data-testid="challenge-day-text">{dayText}</p>
          </div>

          {isAuthed ? (
            active ? (
              <button
                type="button"
                onClick={() => onDone(challenge.id)}
                disabled={busy || active.done_today}
                className={primaryBtn}
                data-testid="challenge-done"
              >
                <span>{active.done_today ? 'Done today ✓' : RULES.done_button}</span>
              </button>
            ) : (
              <button type="button" onClick={() => onJoin(challenge.id)} disabled={busy} className={primaryBtn} data-testid="challenge-join">
                <Icon name="flag" className="text-[18px]" />
                <span>Start this challenge</span>
              </button>
            )
          ) : (
            <Link to="/my-login" className={ghostBtn} data-testid="challenge-signin">Sign in to save your progress</Link>
          )}
        </>
      )}

      {active && !active.complete ? (
        <div className="mt-space-md flex flex-wrap gap-space-xs" data-testid="challenge-days">
          {challenge.days.map((_, i) => (
            <span
              key={i}
              className={`w-7 h-7 rounded-full flex items-center justify-center font-label-sm text-label-sm font-bold ${
                i < (active.done_count || 0)
                  ? 'bg-surface-tint text-on-primary'
                  : 'bg-surface-container-low text-on-surface-variant'
              }`}
            >
              {i + 1}
            </span>
          ))}
        </div>
      ) : null}

      {challenge.support_line ? (
        <p className="mt-space-md font-body-sm text-body-sm text-on-surface-variant" data-testid="challenge-support">
          {challenge.support_line.replace('See helplines.', '')}
          <Link to="/contact#helplines" className="text-surface-tint font-semibold hover:text-primary transition-colors">See helplines.</Link>
        </p>
      ) : null}
    </div>
  );
};

export default ChallengePanel;
